import { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, InputAdornment, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { ApiRequest } from "GlobalFunctions";

interface FaqItem {
  faq_item_id: number;
  question: string;
  answer: string;
  video_url: string | null;
}

interface FaqCategory {
  faq_category_id: number;
  name: string;
  items: FaqItem[];
}

const extractYoutubeId = (url: string): string => {
  if (!url) return "";
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : "";
};

export default function FaqViewer(props: any) {
  const { setLoading } = props;
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<number | false>(false);

  useEffect(() => {
    setLoading(true);
    ApiRequest({
      url: "/faq/public",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        setCategories(response.categories || []);
        setLoading(false);
      },
    });
  }, []);

  const filtered = search.trim()
    ? categories.map(cat => ({
        ...cat,
        items: cat.items.filter(item =>
          item.question.toLowerCase().includes(search.toLowerCase()) ||
          item.answer.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(cat => cat.items.length > 0)
    : categories;

  return (
    <div id="FaqViewer" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Preguntas Frecuentes</h2>
            <span className="h2_subtitle">Encuentra respuestas a las dudas mas comunes</span>
          </div>
        </div>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar en todas las preguntas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" } }}
        />

        {/* Categories + Accordion */}
        {filtered.map((cat) => (
          <div key={cat.faq_category_id} style={{ marginBottom: 24 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5, mb: 1, pl: 0.5 }}
            >
              {cat.name}
            </Typography>
            {cat.items.map((item) => (
              <Accordion
                key={item.faq_item_id}
                expanded={expanded === item.faq_item_id}
                onChange={(_, isExpanded) => setExpanded(isExpanded ? item.faq_item_id : false)}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px !important",
                  mb: 1,
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { mb: 1 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                    {item.answer}
                  </Typography>
                  {item.video_url && extractYoutubeId(item.video_url) && (
                    <div style={{ marginTop: 16, borderRadius: 8, overflow: "hidden", aspectRatio: "16/9", maxWidth: 560 }}>
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${extractYoutubeId(item.video_url)}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={item.question}
                      />
                    </div>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        ))}

        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            {search ? "No se encontraron resultados para la busqueda" : "No hay preguntas frecuentes disponibles"}
          </Typography>
        )}
      </div>
    </div>
  );
}
